import type { $Fetch } from "ofetch";
import { ClsNfse } from "../../classes/NFSe";

/**
 * Nome do Controller da API
 */
const controller = "/nfse";

/**
 * Classe de gerenciamento de chamadas da API dos módulos
 *
 * @class
 */
export default class NFSeApi {
  /**
   * Instância da dependência da API (ofetch)
   */
  private api;

  /**
   * Cria uma nova instância da classe NFSeApi.
   * @constructor
   * @param api - Instância da dependência da API (ofetch).
   */
  constructor(api: $Fetch) {
    this.api = api;
  }

  isMluaAtivo() {
    return this.api<any>(`/NfseNacional/status-mlua`, {
      method: "GET",
    });
  }
  /**
   * Cancela uma nota fiscal.
   */
  cancelInvoice(
    codNfe: number,
    codigoCancelamento: number,
    codModeloNfe: number,
    codUsuario: number,
  ) {

    if (codModeloNfe === 2) {
      return this.api<any>(
        `/NfseNacional/cancelar-nota-fiscal/${codNfe}/${codigoCancelamento}/${codUsuario}`,
        {
          method: "DELETE",
        },
      );
    } else {
      return this.api<any>(
        `/nfse/cancelar-nota-fiscal/${codNfe}/${codigoCancelamento}/${codUsuario}`,
        {
          method: "DELETE",
        },
      );
    }

  }

  /**
   * Consulta uma nota fiscal.
   */
  getInvoice(codNfe: number, codModeloNfe: number, codUsuario: number) {
    if (codModeloNfe === 2) {
      return this.api<any>(
        `/NfseNacional/consultar-nota-fiscal/${codNfe}/${codUsuario}`,
        {
          method: "GET",
        },
      );
    }else{
       return this.api<any>(
        `/nfse/consultar-nota-fiscal/${codNfe}/${codUsuario}`,
        {
          method: "GET",
        },
      );
    }
  }
  getItemListaNBS() {
    return this.api<{ codigo: number, descricao: string, codigomunicipal: number }[]>(`/NfseNacional/item-lista-Nbs`, {
      method: "GET",
    });
  }

  getItemListaServico() {
    return this.api<any>(`/NfseNacional/item-lista-servico`, {
      method: "GET",
    });
  }

  /**
   * Histórico de uma nota fiscal.
   */
  getHistoricoNota(codNfe: number, tomador: string) {
    return this.api<any>(`${controller}/historico-nota/${codNfe}/${tomador}`, {
      method: "GET",
    });
  }

  /**
   * Carrega dados de uma nota fiscal.
   */
  getInvoiceData(codNfe: number) {
    return this.api<any>(`${controller}/dados-nota-fiscal/${codNfe}`, {
      method: "GET",
    });
  }

  /**
   * Lista municípios por UF.
   */
  listMunicipalities(uf: string) {
    return this.api<any>(`${controller}/listar-municipios/${uf}`, {
      method: "GET",
    });
  }

  /**
   * Lista todos os países.
   */
  listCountries() {
    return this.api<any>(`${controller}/listar-paises`, {
      method: "GET",
    });
  }
  getValidarMlua() {
    return this.api<any>(`/NfseNacional/validar-mlua`, {
      method: "GET",
    });
  }

  /**
   * Lista notas fiscais.
   */
  listInvoices(params: {
    codEmissor?: number;
    dataInicio?: string;
    dataFim?: string;
    numeroInicioNfse?: number;
    numeroFimNfse?: number;
    numeroInicioRps?: number;
    numeroFimRps?: number;
    codStatusNfse?: number;
    tipoTomador?: number;
    tomador?: string;
    cpfCnpjTomador?: string;
    isDataCompetencia?: boolean;
  }) {
    const normalizedParams = Object.fromEntries(
      Object.entries(params).map(([key, value]) => [key, value ?? 0]),
    );
    return this.api<any>(`${controller}/listar-notasfiscais`, {
      method: "GET",
      params: normalizedParams,
    });
  }

  /**
   * Gera uma nova NFSe.
   */
  createNFSe(nfse: any) {

    if (Number(nfse.CodModeloNfe) === 2) {
      return this.api<any>(`/NfseNacional/gerar-nfse`, {
        method: "POST",
        body: nfse,
      });

    } else {
      return this.api<any>(`/nfse/gerar-nfse`, {
        method: "POST",
        body: nfse,
      });
    }
  }




  /**
   * Retorna itens da lista de serviço.
   */
  listServiceItems() {
    return this.api<any>(`${controller}/item-lista-servico`, {
      method: "GET",
    });
  }

  /**
   * Retorna indicadores de exigibilidade.
   */
  listIndicatorsOfExigibility() {
    return this.api<any>(`${controller}/indicador-exigibilidade`, {
      method: "GET",
    });
  }

  /**
   * Retorna regimes especiais de tributação.
   */
  listSpecialTaxRegimes() {
    return this.api<any>(`${controller}/regime-especial-tributacao`, {
      method: "GET",
    });
  }

  /**
   * Retorna códigos do regime tributário.
   */
  listTaxRegimes() {
    return this.api<any>(`${controller}/regime-tributario`, {
      method: "GET",
    });
  }

  /**
   * Retorna responsáveis pela retenção.
   */
  listRetentionResponsibles() {
    return this.api<any>(`${controller}/responsavel-retencao`, {
      method: "GET",
    });
  }

  /**
   * Gera impressão da nota fiscal.
   * @param codNfe Código da nota fiscal.
   */
  gerarImpressao(codNfe: number) {
    return this.api<Blob>(`${controller}/gerar-pdf/${codNfe}`, {
      method: "GET",
      responseType: "blob" as any,
      retry: 0,
    });
  }

  /**
   * Gera impressão em lote da nota fiscal.
   * @param codNfe Código da nota fiscal.
   */
  gerarImpressaoemLote(codNfe: number[]) {
    return this.api<any>(`${controller}/gerar-pdfs`, {
      method: "POST",
      body: codNfe,
    });
  }

  /**
   * Envia um e-mail com informações de uma nota fiscal.
   *
   * Faz uma chamada ao repositório para enviar um e-mail referente à nota fiscal
   * com o código fornecido. Inclui detalhes como o código da aplicação, endereço
   * do destinatário, assunto e mensagem do e-mail.
   *
   * @param {number} codNfe - Código da nota fiscal.
   * @param {number} codAplicacao - Código da aplicação.
   * @param {string} emailDestinatario - Endereço de e-mail do destinatário.
   * @param {string} assunto - Assunto do e-mail.
   * @param {string} mensagem - Corpo da mensagem do e-mail.
   * @returns {Promise<any>} A resposta do repositório ou null em caso de erro.
   */
  enviarEmail(
    codNfe: number,
    codAplicacao: number,
    emailDestinatario: string,
    assunto: string,
    mensagem: string,
  ) {
    return this.api<any>(`${controller}/enviar-email`, {
      method: "GET",
      params: { codNfe, codAplicacao, emailDestinatario, assunto, mensagem },
    });
  }

  /**
   * Testa se um e-mail pode ser enviado para o destinatário com o código da aplicação especificado.
   *
   * Faz uma chamada ao repositório para testar se um e-mail pode ser enviado com
   * o código da aplicação e endereço de e-mail do destinatário especificados.
   *
   * @param {number} codAplicacao - Código da aplicação.
   * @param {string} emailDestinatario - Endereço de e-mail do destinatário.
   * @returns {Promise<any>} A resposta do repositório ou undefined em caso de erro.
   */
  testarEmail(codAplicacao: number, emailDestinatario: string) {
    return this.api<any>(`${controller}/testar-email`, {
      method: "GET",
      params: { codAplicacao, emailDestinatario },
    });
  }

  /**
   * Busca a aplicação pelo registro da aplicação.
   *
   * Faz uma chamada ao repositório para buscar a aplicação
   * correspondente ao registro fornecido.
   *
   * @param {string} registroAplicacao - Registro da aplicação a ser buscada.
   * @returns {Promise<any>} A aplicação encontrada ou null em caso de erro.
   */
  buscarCodAplicacao(registroAplicacao: string) {
    return this.api<any>(`aplicacao/buscarAplicacao/${registroAplicacao}`, {
      method: "GET",
    });
  }

  /**
   * Lista os emails associados a uma aplicação.
   *
   * Faz uma chamada ao repositório para buscar a lista de emails
   * associados a uma aplicação pelo código da aplicação.
   *
   * @param {number} codAplicacao - Código da aplicação.
   * @returns {Promise<any>} A lista de emails associados a aplicação ou null em caso de erro.
   */
  emailListar(codAplicacao: number) {
    return this.api<any>(`email/listar`, {
      method: "GET",
      params: { codAplicacao },
    });
  }

  /**
   * Cadastra um novo e-mail associado a uma aplicação.
   *
   * Faz uma chamada ao repositório para cadastrar um novo e-mail
   * fornecido no corpo da requisição.
   *
   * @param {any} enviarEmailDto - Objeto contendo os detalhes do e-mail a ser cadastrado.
   * @returns {Promise<any>} A resposta do repositório ou null em caso de erro.
   */

  emailCadastrar(enviarEmailDto: any) {
    return this.api<any>(`email/cadastrar`, {
      method: "POST",
      body: enviarEmailDto,
    });
  }
}
